export type WebViewMode = "mobile" | "desktop";

type WebViewModeSignals = {
  pathname: string;
  search: string;
  userAgent: string;
  userAgentMobile?: boolean;
  maxTouchPoints?: number;
};

const MOBILE_BROWSER_PATTERN =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Silk|Kindle/i;

export function isDesktopEmbeddedSearch(search: string) {
  return (
    new URLSearchParams(search).get("embedded")?.toLowerCase() === "desktop"
  );
}

export function isDesktopEmbeddedWeb() {
  return (
    typeof window !== "undefined" &&
    isDesktopEmbeddedSearch(window.location.search)
  );
}

export function resolveWebViewMode({
  pathname,
  search,
  userAgent,
  userAgentMobile = false,
  maxTouchPoints = 0,
}: WebViewModeSignals): WebViewMode {
  const searchParams = new URLSearchParams(search);
  const requestedView = searchParams.get("view")?.toLowerCase();
  if (requestedView === "mobile") return "mobile";
  if (requestedView === "desktop") return "desktop";

  // The desktop short-video tab embeds the mobile feed in an iframe. Treat
  // that embedded surface as mobile even when the iframe inherits a desktop
  // user agent, otherwise it recursively renders another DesktopHome shell.
  if (isDesktopEmbeddedSearch(search)) {
    return "mobile";
  }

  if (/\/pc\/?$/.test(pathname)) return "desktop";

  const iPadDesktopUserAgent =
    /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
  if (
    userAgentMobile ||
    iPadDesktopUserAgent ||
    MOBILE_BROWSER_PATTERN.test(userAgent)
  ) {
    return "mobile";
  }

  return "desktop";
}

export function detectWebViewMode(): WebViewMode {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "mobile";
  }
  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };
  return resolveWebViewMode({
    pathname: window.location.pathname,
    search: window.location.search,
    userAgent: navigator.userAgent || "",
    userAgentMobile: navigatorWithUserAgentData.userAgentData?.mobile === true,
    maxTouchPoints: navigator.maxTouchPoints || 0,
  });
}
